// noinspection DuplicatedCode
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Colors } from "../../../assets/Colors";
import { fetchGet, fetchPost } from "../../../fetchers";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DynamicTableExtendAdjustStock } from "../../../components/tablecard/DynamicTable";
import { Card } from "@material-tailwind/react";
import "react-toastify/dist/ReactToastify.css";
import { maxPageGlobal } from "../../../assets/config";
import { toast } from "react-toastify";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import { useForm } from "../../../components/isRequired";
import Skeltons from "../../../components/loading/Skeltons";
import ModalFooter from "@material-tailwind/react/ModalFooter";
import Modal from "@material-tailwind/react/Modal";
import ModalHeader from "@material-tailwind/react/ModalHeader";
import ModalBody from "@material-tailwind/react/ModalBody";
import Button from "@material-tailwind/react/Button";
import debounce from "lodash/debounce";
import { BiSearch, BiRefresh } from "react-icons/bi";
import { FaEdit } from "react-icons/fa";

// Constants
const INITIAL_STATE = {
  Bulan: new Date(),
  BisnisUnitID: "U0002",
  BarangID: "",
  NamaBarang: "",
  SaldoAkhir: 0,
};

const VALIDATIONS = [
  ({ BisnisUnitID }) =>
    !BisnisUnitID && { BisnisUnitID: "Bisnis Unit wajib dipilih" },
  ({ Bulan }) => !Bulan && { Bulan: "Periode wajib dipilih" },
  ({ SaldoAkhir }) =>
    SaldoAkhir < 0 && { SaldoAkhir: "Saldo Akhir tidak boleh negatif" },
];

const INPUT_BUILDER = [
  {
    type: "text",
    name: "BarangID",
    title: "Barang ID",
    disabled: true,
    edit: true,
  },
  {
    type: "text",
    name: "NamaBarang",
    title: "Nama Barang",
    disabled: true,
    edit: true,
  },
  {
    type: "number",
    name: "SaldoAkhir",
    title: "Qty",
    disabled: false,
    edit: true,
  },
];

const HEADERS = [
  { name: "Barang ID", key: "BarangID", modifiers: "Text" },
  { name: "Kode Stock Opname", key: "KodeStockOpname", modifiers: "Text" },
  { name: "Barang", key: "NamaBarang", modifiers: "Text" },
  { name: "Satuan", key: "NamaSatuan", modifiers: "Text" },
  { name: "Saldo Akhir", key: "SaldoAkhir", modifiers: "number" },
  {
    name: "SaldoAkhirNominal",
    key: "SaldoAkhirNominal",
    modifiers: "SaldoAkhirNominal",
  },
  {
    name: "Edit",
    key: "Edit",
    modifiers: "Button",
  },
];

const AdjustStockPage = () => {
  // State management
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(maxPageGlobal);
  const [bisnisUnit, setBisnisUnit] = useState("");
  const [showAll, setShowAll] = useState(false); // Tambahkan state showAll
  const [periode, setPeriode] = useState({
    bulan: (new Date().getMonth() + 1).toString(),
    tahun: new Date().getFullYear().toString(),
  });
  const [showModal, setShowModal] = useState(false);
  const [detail, setDetail] = useState({});
  const [buttonTouched, setButtonTouched] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isSearching, setIsSearching] = useState(false);

  // Form handling untuk filter dan edit
  const {
    changed,
    values,
    isValid,
    touched,
    clearHandler,
    customHandler,
    changeHandler,
    errors,
    editHandler,
  } = useForm(INITIAL_STATE, VALIDATIONS);

  // Debounced search
  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((value) => {
        console.log("Debounced search value:", value);
        setSearchQuery(value);
        setPage(1);
        setIsSearching(false);
      }, 600),
    [],
  );

  // Cleanup debounce
  useEffect(() => {
    return () => debouncedSetSearchQuery.cancel();
  }, [debouncedSetSearchQuery]);

  // Handler untuk search input
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchInput(value);

      debouncedSetSearchQuery.cancel();

      if (value.trim() === "") {
        setSearchQuery("");
        setPage(1);
        setIsSearching(false);
      } else {
        setIsSearching(true);
        debouncedSetSearchQuery(value);
      }
    },
    [debouncedSetSearchQuery],
  );

  // Handler untuk clear search
  const handleClearSearch = useCallback(() => {
    debouncedSetSearchQuery.cancel();
    setSearchInput("");
    setSearchQuery("");
    setPage(1);
    setIsSearching(false);

    toast.info("Pencarian dibersihkan", {
      position: "top-center",
      autoClose: 800,
    });
  }, [debouncedSetSearchQuery]);

  // Handler untuk show all
  const handleShowAll = useCallback(
    (e) => {
      const checked = e.target.checked;
      setShowAll(checked);
      setPage(1);
      setSearchInput("");
      setSearchQuery("");
      setIsSearching(false);
      debouncedSetSearchQuery.cancel();

      if (checked) {
        toast.info("Menampilkan semua periode", {
          position: "top-center",
          autoClose: 1000,
        });
      }
    },
    [debouncedSetSearchQuery],
  );

  // Update bisnis unit ketika nilai form berubah
  useEffect(() => {
    if (values.BisnisUnitID) {
      setBisnisUnit(values.BisnisUnitID);
      setPage(1);
    }
  }, [values.BisnisUnitID]);

  // Update periode ketika nilai form berubah (hanya jika tidak showAll)
  useEffect(() => {
    if (values.Bulan && !showAll) {
      try {
        const selectedDate = new Date(values.Bulan);
        if (!isNaN(selectedDate.getTime())) {
          const bulan = (selectedDate.getMonth() + 1).toString();
          const tahun = selectedDate.getFullYear().toString();
          setPeriode({ bulan, tahun });
          setPage(1);
        }
      } catch (error) {
        console.error("Error parsing date:", error);
      }
    }
  }, [values.Bulan, showAll]);

  // Fetch data dengan React Query - Mengikuti pola StockPage
  const {
    data: stockData,
    isLoading,
    isFetching,
    refetch,
    error,
  } = useQuery({
    queryKey: [
      "adjustStock",
      bisnisUnit,
      showAll,
      periode.bulan,
      periode.tahun,
      page,
      limit,
      searchQuery,
    ],
    queryFn: async () => {
      if (!bisnisUnit) return { data: [], count: 0 };

      // Tentukan endpoint berdasarkan showAll - SAMA PERSIS dengan StockPage
      let endpoint = "";
      if (showAll) {
        endpoint = `Procurement/GoodsStock/BuOnly/${bisnisUnit}`;
      } else {
        endpoint = `Procurement/GoodsStock/${bisnisUnit}/${periode.bulan}/${periode.tahun}`;
      }

      const params = new URLSearchParams();
      if (searchQuery && searchQuery.trim() !== "") {
        params.append("search", searchQuery.trim());
      }
      params.append("page", page.toString());
      params.append("limit", limit.toString());

      const url = `${endpoint}?${params.toString()}`;
      console.log("Fetching with endpoint:", url);

      const response = await fetchGet(url);

      if (isFirstLoad) {
        setIsFirstLoad(false);
      }

      return response;
    },
    keepPreviousData: true,
    staleTime: 30000,
    enabled: !!bisnisUnit,
  });

  // Mutation untuk menyimpan data - DENGAN PERIODE BULAN DAN TAHUN (format alternatif)
  const saveMutation = useMutation({
    mutationFn: (formData) => {
      // Ambil bulan dan tahun dari nilai Bulan di form
      let bulan = "";
      let tahun = "";
      let periode = "";

      if (formData.Bulan) {
        try {
          const selectedDate = new Date(formData.Bulan);
          if (!isNaN(selectedDate.getTime())) {
            bulan = (selectedDate.getMonth() + 1).toString();
            tahun = selectedDate.getFullYear().toString();
            periode = `${bulan}/${tahun}`; // Format "MM/YYYY"
          }
        } catch (error) {
          console.error("Error parsing date for payload:", error);
        }
      }

      const payload = {
        BarangID: formData.BarangID,
        SaldoAkhir: parseFloat(formData.SaldoAkhir) || 0,
        Bulan: bulan,
        Tahun: tahun,
        Periode: periode, // Tambahkan periode dalam format "MM/YYYY"
      };

      console.log("Saving with payload:", payload);
      return fetchPost("Procurement/SaveAjustStock", payload);
    },
    onSuccess: (responseData) => {
      console.log("✅ SaveAjustStock SUCCESS:", responseData);

      setShowModal(false);
      toast.success("Data berhasil disimpan!", {
        position: "top-center",
        autoClose: 2000,
      });

      // Refresh data
      refetch();
      clearHandler();
    },
    onError: (error) => {
      console.error("❌ SaveAjustStock ERROR:", error);
      toast.error(
        "Gagal menyimpan data: " +
          (error.response?.data?.message || error.message),
        {
          position: "top-center",
          autoClose: 3000,
        },
      );
    },
  });

  // Handler untuk filter
  const handleFilter = useCallback(() => {
    if (!values.BisnisUnitID) {
      toast.error("Pilih Bisnis Unit terlebih dahulu", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }

    try {
      // Validasi date hanya jika tidak showAll
      if (!showAll) {
        const selectedDate = new Date(values.Bulan);
        if (isNaN(selectedDate.getTime())) {
          toast.error("Format tanggal tidak valid", {
            position: "top-center",
            autoClose: 2000,
          });
          return;
        }

        const bulan = (selectedDate.getMonth() + 1).toString();
        const tahun = selectedDate.getFullYear().toString();
        setPeriode({ bulan, tahun });
      }

      setBisnisUnit(values.BisnisUnitID);
      setPage(1);
      setSearchInput("");
      setSearchQuery("");
      setIsSearching(false);
      debouncedSetSearchQuery.cancel();

      const filterMessage = showAll
        ? `Menampilkan semua periode untuk Bisnis Unit ${values.BisnisUnitID}`
        : `Filter diterapkan: Bisnis Unit ${values.BisnisUnitID} - Bulan ${periode.bulan} Tahun ${periode.tahun}`;

      toast.success(filterMessage, {
        position: "top-center",
        autoClose: 1000,
      });
    } catch (error) {
      console.error("Error in handleFilter:", error);
      toast.error("Terjadi kesalahan saat memproses filter", {
        position: "top-center",
        autoClose: 2000,
      });
    }
  }, [values, showAll, periode, debouncedSetSearchQuery]);

  // Handler untuk refresh
  const handleRefresh = useCallback(() => {
    refetch();
    toast.info("Memuat ulang data...", {
      position: "top-center",
      autoClose: 1000,
    });
  }, [refetch]);

  // Handler untuk pagination
  const handleUpdatePage = useCallback((newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Handler untuk change limit per page
  const handleUpdateCount = useCallback((newLimit) => {
    const newLimitValue = newLimit === "-1" ? 999999 : parseInt(newLimit);
    setLimit(newLimitValue);
    setPage(1);
  }, []);

  // Handler untuk membuka modal edit
  const ModalOpener = useCallback(
    (detail) => {
      setShowModal(true);
      setDetail(detail);

      // Set values untuk form edit
      editHandler({
        ...values, // Pertahankan nilai Bulan dan BisnisUnitID yang sudah ada
        BarangID: detail.BarangID,
        NamaBarang: detail.NamaBarang,
        SaldoAkhir: detail.SaldoAkhir || 0,
      });
    },
    [editHandler, values],
  );

  // Handler untuk submit form
  const handleSubmit = useCallback(() => {
    if (!isValid && changed) {
      setButtonTouched(true);
      return;
    }

    saveMutation.mutate(values);
  }, [isValid, changed, values, saveMutation]);

  // Data preparation
  const tableData = stockData?.data || [];
  const totalRows = stockData?.count || 0;
  const isLoadingData = isLoading || isFetching;

  // Calculate max page
  const maxPage = useMemo(() => {
    if (!totalRows) return 1;
    return Math.ceil(totalRows / limit) || 1;
  }, [totalRows, limit]);

  // Tampilkan error jika ada
  if (error) {
    return (
      <div className="md:ml-64">
        <div
          style={{ background: Colors.primary }}
          className="pt-14 pb-16 px-3 md:px-8 h-auto"
        >
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
          </div>
        </div>
        <div className="px-3 md:px-8 h-auto -mt-32">
          <div className="container mx-auto max-w-full">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              Error: {error.message}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="md:ml-64">
      <div
        style={{ background: Colors.primary }}
        className="pt-14 pb-16 px-3 md:px-8 h-auto"
      >
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
        </div>
      </div>

      <div className="px-3 md:px-8 h-auto -mt-32">
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 px-4">
            <Card>
              <div className="p-6">
                {/* Filter Section - Mengikuti pola StockPage */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-4 items-end">
                    {/* Bisnis Unit */}
                    <div className="w-full md:w-64">
                      <HrdInputGenerator
                        type="BisnisUnit"
                        name="BisnisUnitID"
                        title="Bisnis Unit"
                        value={values}
                        required={true}
                        onChange={customHandler}
                        touched={touched}
                        errors={errors}
                        size="full"
                      />
                    </div>

                    {/* Periode - hanya aktif jika tidak showAll */}
                    <div className="w-full md:w-64">
                      <HrdInputGenerator
                        type="bulan"
                        name="Bulan"
                        title="Periode"
                        value={values}
                        required={!showAll}
                        onChange={customHandler}
                        touched={touched}
                        errors={errors}
                        size="full"
                        disabled={showAll}
                      />
                    </div>

                    {/* Show All Checkbox - SAMA PERSIS dengan StockPage */}
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="showAll"
                        onChange={handleShowAll}
                        checked={showAll}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <label
                        htmlFor="showAll"
                        className="ml-2 text-sm text-gray-700"
                      >
                        Show All Periode
                      </label>
                    </div>

                    {/* Filter Button */}
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={handleFilter}
                        className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                      >
                        Tampilkan Data
                      </button>
                    </div>

                    {/* Refresh Button */}
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={handleRefresh}
                        disabled={isLoadingData}
                        className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-500 text-white hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        <BiRefresh
                          className={`inline mr-1 ${isFetching ? "animate-spin" : ""}`}
                        />
                        Refresh
                      </button>
                    </div>

                    {/* Info Loading */}
                    {isFetching && !isFirstLoad && (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                        <span className="ml-2 text-sm text-gray-500">
                          Memuat...
                        </span>
                      </div>
                    )}

                    {/* Info Total Data */}
                    {totalRows > 0 && (
                      <div className="text-sm text-gray-600">
                        Total: {totalRows} data
                        {searchQuery && ` (hasil pencarian "${searchQuery}")`}
                      </div>
                    )}
                  </div>
                </div>

                {/* Info Filter yang Ditampilkan - SAMA PERSIS dengan StockPage */}
                {bisnisUnit && (
                  <div className="mb-4 px-4 py-2 bg-blue-50 border rounded-md">
                    <p className="text-sm text-blue-700">
                      {showAll ? (
                        <>
                          Menampilkan <strong>SEMUA PERIODE</strong> untuk
                          Bisnis Unit: <strong>{bisnisUnit}</strong>
                        </>
                      ) : (
                        <>
                          Menampilkan periode:{" "}
                          <strong>
                            Bulan {periode.bulan} Tahun {periode.tahun}
                          </strong>{" "}
                          | Bisnis Unit: <strong>{bisnisUnit}</strong>
                        </>
                      )}
                      {searchQuery && (
                        <span className="ml-2">
                          | Pencarian: <strong>"{searchQuery}"</strong>
                        </span>
                      )}
                    </p>
                  </div>
                )}

                {/* Search Bar */}
                <div className="mb-4">
                  <div className="relative w-full md:w-96">
                    <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Cari Barang ID, Nama Barang, Kode Stock..."
                      value={searchInput}
                      onChange={handleSearchChange}
                      disabled={!bisnisUnit}
                      className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    {searchInput && (
                      <button
                        onClick={handleClearSearch}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        title="Clear search"
                      >
                        ✕
                      </button>
                    )}

                    {isSearching && (
                      <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                        <div className="flex items-center space-x-1">
                          <div
                            className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce"
                            style={{ animationDelay: "0ms" }}
                          ></div>
                          <div
                            className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce"
                            style={{ animationDelay: "150ms" }}
                          ></div>
                          <div
                            className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce"
                            style={{ animationDelay: "300ms" }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Status Pencarian */}
                  {searchInput !== searchQuery && searchInput && (
                    <p className="text-xs text-blue-500 mt-1 flex items-center">
                      <span className="animate-pulse mr-1">●</span>
                      Mencari "{searchInput}"...
                    </p>
                  )}

                  {searchQuery && !isSearching && (
                    <p className="text-xs text-green-600 mt-1 flex items-center">
                      <span className="mr-1">✓</span>
                      Menampilkan hasil untuk "{searchQuery}"
                    </p>
                  )}
                </div>

                {/* Table Component */}
                {isLoadingData && isFirstLoad ? (
                  <div className="w-full">
                    <Skeltons />
                  </div>
                ) : (
                  <DynamicTableExtendAdjustStock
                    title="Adjust Stock"
                    data={stockData || { data: [], count: 0 }}
                    header={HEADERS}
                    Master={false}
                    value={searchInput}
                    currentPage={page}
                    maxPage={maxPage}
                    name="Adjust Stock"
                    OnClick={ModalOpener}
                    updateCount={handleUpdateCount}
                    count={limit}
                    updatePage={handleUpdatePage}
                  />
                )}

                {/* Pesan jika belum pilih bisnis unit */}
                {!bisnisUnit && (
                  <div className="text-center py-8 text-gray-500">
                    Silakan pilih Bisnis Unit dan periode terlebih dahulu
                  </div>
                )}
              </div>
            </Card>

            {/* Modal Adjust */}
            <Modal
              size="lg"
              active={showModal}
              toggler={() => {
                setShowModal(false);
                clearHandler();
              }}
            >
              <ModalHeader
                toggler={() => {
                  setShowModal(false);
                  clearHandler();
                }}
              >
                <div className="flex items-center">
                  <FaEdit className="mr-2 text-blue-600" />
                  Adjust Stock
                </div>
              </ModalHeader>
              <ModalBody>
                <form className="w-full" onSubmit={(e) => e.preventDefault()}>
                  <div className="space-y-4">
                    {INPUT_BUILDER.map((input, index) => (
                      <div key={`${input.name}-${index}`} className="mb-4">
                        <HrdInputGenerator
                          type={input.type}
                          value={values}
                          name={input.name}
                          title={input.title}
                          onChange={changeHandler}
                          touched={touched}
                          buttonTouched={buttonTouched}
                          size={input.size || "full"}
                          required={input.required}
                          errors={errors}
                          disabled={input.disabled}
                        />
                      </div>
                    ))}
                  </div>
                </form>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="red"
                  buttonType="link"
                  onClick={() => {
                    setShowModal(false);
                    clearHandler();
                  }}
                  ripple="dark"
                >
                  Batal
                </Button>

                <Button
                  color={isValid ? "green" : "blueGray"}
                  disabled={saveMutation.isLoading || (!isValid && changed)}
                  onClick={handleSubmit}
                  ripple={isValid ? "light" : ""}
                >
                  {saveMutation.isLoading ? (
                    <div className="flex items-center">
                      <svg
                        aria-hidden="true"
                        role="status"
                        className="inline mr-2 w-4 h-4 text-white animate-spin"
                        viewBox="0 0 100 101"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                          fill="#E5E7EB"
                        />
                        <path
                          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                          fill="currentColor"
                        />
                      </svg>
                      Menyimpan...
                    </div>
                  ) : (
                    "Simpan"
                  )}
                </Button>
              </ModalFooter>
            </Modal>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdjustStockPage;
